/**
 * Avatar - Reusable user avatar component
 *
 * Features:
 * - Displays profile picture if available
 * - Falls back to colored initials
 * - Multiple size variants
 * - Consistent color generation from name
 */
import React from 'react';
import './Avatar.css';

/**
 * Avatar component
 *
 * @param {Object} user - User object with avatar_url, first_name, last_name, email
 * @param {string} size - Size variant: xs, sm, md, lg, xl (default: md)
 * @param {string} className - Additional CSS classes
 * @param {string} alt - Alt text override (defaults to user's name)
 */
export const Avatar = ({ user, size = 'md', className = '', alt = null }) => {
  const sizes = {
    xs: 24,
    sm: 32,
    md: 40,
    lg: 48,
    xl: 64,
    '2xl': 80
  };

  const dimension = typeof size === 'number' ? size : (sizes[size] || sizes.md);

  /**
   * Get user's initials
   */
  const getInitials = () => {
    if (!user) return '?';

    // Try first + last name
    const first = user.first_name?.trim()?.[0] || '';
    const last = user.last_name?.trim()?.[0] || '';

    if (first && last) {
      return (first + last).toUpperCase();
    }

    // Fallback to email
    if (user.email) {
      return user.email[0].toUpperCase();
    }

    return '?';
  };

  /**
   * Get full name for alt text and title
   */
  const getFullName = () => {
    if (!user) return 'User';

    if (user.first_name && user.last_name) {
      return `${user.first_name} ${user.last_name}`.trim();
    }

    return user.email || 'User';
  };

  /**
   * Generate consistent color from user's name/email
   */
  const getColorFromName = () => {
    const name = user?.email || user?.first_name || user?.last_name || 'default';

    // Predefined color palette - professional and accessible
    const colors = [
      '#6554c0', // Purple
      '#36b37e', // Green
      '#ff5630', // Red
      '#ffab00', // Yellow
      '#4c9aff', // Blue
      '#00b8d9', // Cyan
      '#ff991f', // Orange
      '#00875a', // Dark Green
      '#403294', // Deep Purple
      '#de350b'  // Dark Red
    ];

    // Generate index from character codes
    let hash = 0;
    for (let i = 0; i < name.length; i++) {
      hash = name.charCodeAt(i) + ((hash << 5) - hash);
    }

    const index = Math.abs(hash) % colors.length;
    return colors[index];
  };

  const altText = alt || getFullName();

  // Render image avatar
  if (user?.avatar_url) {
    return (
      <img
        src={user.avatar_url}
        alt={altText}
        title={getFullName()}
        className={`avatar avatar-${size} ${className}`}
        style={{ width: dimension, height: dimension }}
      />
    );
  }

  // Render initials avatar
  return (
    <div
      className={`avatar avatar-${size} avatar-initials ${className}`}
      style={{
        width: dimension,
        height: dimension,
        backgroundColor: getColorFromName(),
        fontSize: size === 'xs' ? '10px' : size === 'sm' ? '12px' : size === 'lg' ? '18px' : size === 'xl' ? '24px' : size === '2xl' ? '32px' : '14px'
      }}
      title={getFullName()}
      aria-label={altText}
    >
      {getInitials()}
    </div>
  );
};

/**
 * AvatarGroup - Display multiple avatars in a stack
 *
 * @param {Array} users - Array of user objects
 * @param {string} size - Size variant
 * @param {number} max - Maximum avatars to display (default: 3)
 */
export const AvatarGroup = ({ users = [], size = 'sm', max = 3, className = '' }) => {
  if (!users || users.length === 0) return null;

  const visibleUsers = users.slice(0, max);
  const remainingCount = users.length - max;

  return (
    <div className={`avatar-group avatar-group-${size} ${className}`}>
      {visibleUsers.map((user, index) => (
        <Avatar key={user.id || index} user={user} size={size} />
      ))}
      {remainingCount > 0 && (
        <div
          className={`avatar avatar-${size} avatar-more`}
          title={`+${remainingCount} more`}
        >
          +{remainingCount}
        </div>
      )}
    </div>
  );
};

export default Avatar;
